import React, { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';

export default function NeonBall() {
  const containerRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width - 0.5) * 30;
        const y = ((e.clientY - rect.top) / rect.height - 0.5) * 30;
        setMousePosition({ x, y });
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('mousemove', handleMouseMove);
    }

    return () => {
      if (container) {
        container.removeEventListener('mousemove', handleMouseMove);
      }
    };
  }, []);

  return (
    <div 
      ref={containerRef}
      className="w-full h-full min-h-[300px] md:min-h-[400px] relative flex items-center justify-center"
      data-testid="neon-ball-container"
    >
      {/* Main animated orb container */}
      <motion.div
        className="relative"
        animate={{
          x: mousePosition.x,
          y: mousePosition.y,
        }}
        transition={{ type: 'spring', stiffness: 100, damping: 20 }}
      >
        {/* Outer glow ring */}
        <motion.div
          className="absolute inset-0 -m-8"
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
        >
          <div className="w-full h-full rounded-full border-2 border-[var(--primary-light)] opacity-30" 
               style={{ width: '280px', height: '280px', position: 'relative', left: '-20px', top: '-20px' }} />
        </motion.div>

        {/* Secondary ring */}
        <motion.div
          className="absolute inset-0 -m-4"
          animate={{ rotate: -360 }}
          transition={{ duration: 15, repeat: Infinity, ease: 'linear' }}
        >
          <div className="w-full h-full rounded-full border border-[var(--accent-gold)] opacity-20" 
               style={{ width: '260px', height: '260px', position: 'relative', left: '-10px', top: '-10px' }} />
        </motion.div>

        {/* Main orb */}
        <motion.div
          className="relative w-[240px] h-[240px] rounded-full"
          animate={{ 
            scale: [1, 1.03, 1],
          }}
          transition={{ 
            duration: 4, 
            repeat: Infinity, 
            ease: 'easeInOut' 
          }}
          style={{
            background: 'radial-gradient(circle at 30% 30%, #8B5CF6 0%, #7C3AED 40%, #5B21B6 80%, #3B1596 100%)',
            boxShadow: `
              0 0 60px rgba(124, 58, 237, 0.5),
              0 0 100px rgba(124, 58, 237, 0.3),
              0 0 140px rgba(124, 58, 237, 0.2),
              inset 0 0 60px rgba(139, 92, 246, 0.5),
              inset -30px -30px 60px rgba(91, 33, 182, 0.8)
            `,
          }}
        >
          {/* Gold highlight */}
          <div 
            className="absolute top-[15%] left-[15%] w-[35%] h-[35%] rounded-full"
            style={{
              background: 'radial-gradient(circle at 50% 50%, rgba(255, 215, 0, 0.3) 0%, transparent 70%)',
              filter: 'blur(8px)',
            }}
          />
          
          {/* Shine effect */}
          <motion.div
            className="absolute top-[10%] left-[20%] w-[20%] h-[20%] rounded-full"
            animate={{ opacity: [0.6, 0.9, 0.6] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            style={{
              background: 'radial-gradient(circle, rgba(255,255,255,0.4) 0%, transparent 70%)',
            }}
          />
          
          {/* Secondary shine */}
          <div 
            className="absolute top-[25%] left-[25%] w-[8%] h-[8%] rounded-full bg-white opacity-50"
            style={{ filter: 'blur(2px)' }}
          />
        </motion.div>

        {/* Inner glow */}
        <motion.div
          className="absolute inset-0 flex items-center justify-center"
          animate={{ opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        >
          <div 
            className="w-[180px] h-[180px] rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(255, 215, 0, 0.15) 0%, transparent 70%)',
            }}
          />
        </motion.div>

        {/* Floating particles */}
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 rounded-full"
            style={{
              background: i % 2 === 0 ? 'var(--primary-light)' : 'var(--accent-gold)',
              left: '50%',
              top: '50%',
            }}
            animate={{
              x: [0, Math.cos(i * 60 * Math.PI / 180) * 150],
              y: [0, Math.sin(i * 60 * Math.PI / 180) * 150],
              opacity: [0, 0.6, 0],
              scale: [0.5, 1, 0.5],
            }}
            transition={{
              duration: 3 + i * 0.5,
              repeat: Infinity,
              delay: i * 0.5,
              ease: 'easeOut',
            }}
          />
        ))}
      </motion.div>

      {/* Background glow effects */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full"
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            background: 'radial-gradient(circle, rgba(124, 58, 237, 0.2) 0%, transparent 70%)',
            filter: 'blur(40px)',
          }}
        />
        <motion.div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full"
          animate={{ scale: [1.1, 1, 1.1] }}
          transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            background: 'radial-gradient(circle, rgba(255, 215, 0, 0.1) 0%, transparent 70%)',
            filter: 'blur(30px)',
          }}
        />
      </div>
    </div>
  );
}
